import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ColumnDef } from '@tanstack/react-table'
import { DataTable, SortableHeader } from '@/components/ui/data-table'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useQuery } from '@tanstack/react-query'
import { adminQueries } from '@/services/adminApi'
import { AdminAccount } from '@/services/adminApi'
import { CurrencyDisplay } from '@/components/ui/currency-display'

const columns: ColumnDef<AdminAccount, any>[] = [
  {
    accessorKey: 'id',
    header: ({ column }) => <SortableHeader column={column} label="ID" />,
    cell: ({ row }) => {
      const id = row.original.id
      return <span className="font-mono text-xs">{id}</span>
    },
  },
  {
    accessorKey: 'email',
    header: ({ column }) => <SortableHeader column={column} label="Email" />,
  },
  {
    id: 'balance',
    header: 'Balance',
    cell: ({ row }) => {
      const account = row.original
      return <CurrencyDisplay amount={account.balance.amount} currency={account.balance.currency} inCents />
    },
  },
  {
    accessorKey: 'role',
    header: ({ column }) => <SortableHeader column={column} label="Role" />,
    cell: ({ row }) => {
      const role = row.original.role
      return (
        <Badge variant={role === 'admin' ? 'default' : 'secondary'}>
          {role}
        </Badge>
      )
    },
  },
  {
    id: 'createdAt',
    header: 'Created',
    cell: ({ row }) => {
      const date = row.original.createdAt
      if (!date) return '-'
      return new Date(date).toLocaleDateString()
    },
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => {
      const account = row.original
      return (
        <Link
          to="/admin/users/$accountId"
          params={{ accountId: account.id }}
          className="text-sm text-primary underline-offset-4 hover:underline"
        >
          View
        </Link>
      )
    },
  },
]

export const Route = createFileRoute('/admin/users/')({
  component: AdminUsersPage,
})

function AdminUsersPage() {
  const [page, setPage] = useState(1)
  const pageSize = 20
  const { data, isLoading } = useQuery(adminQueries.accounts(page, pageSize))

  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold">Users</h1>
      <Card flush className="overflow-hidden">
        <CardHeader>
          <CardTitle>All Users</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-96 w-full" />
          ) : (
            <DataTable
              columns={columns}
              data={data?.accounts ?? []}
              enableSearch={false}
              manualPagination={{
                pageIndex: page,
                pageSize,
                totalItems: data?.totalItems ?? 0,
                onPageChange: setPage,
                isLoading,
              }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
